import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Heart, MessageCircle, Share2, Mic, Film, Headphones, Users, UserPlus, Bookmark, TrendingUp, Eye, Play, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import MonetizationSection from '../components/MonetizationSection';
import { videoTexts } from '../components/videos/videoTranslations';

// Safer fallback wrapper: most i18n libs return the key on miss → treat that as "missing".
const tr = (t, key, fallback) => {
  const v = t(key);
  return !v || v === key ? fallback : v;
};

const StatCard = ({ icon: Icon, label, value, accent = 'magenta', testId }) => {
  const accentMap = {
    magenta: 'from-magenta-500 to-pink-500',
    cyan: 'from-cyan-500 to-blue-500',
    green: 'from-emerald-500 to-green-500',
    amber: 'from-amber-500 to-orange-500',
    violet: 'from-violet-500 to-purple-500',
  };
  const grad = accentMap[accent] || accentMap.magenta;
  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-gray-400 font-medium">{label}</div>
        <div className="text-2xl font-bold tabular-nums truncate">{value?.toLocaleString('fr-FR') ?? '—'}</div>
      </div>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="text-lg font-semibold flex items-center gap-2 mb-3 mt-6">
    <Icon className="w-5 h-5 text-magenta-400" />
    {children}
  </h2>
);

const formatMemberSince = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
  } catch {
    return null;
  }
};

export default function Statistics() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get('/api/users/me/statistics');
        if (active) setStats(res.data);
      } catch (err) {
        console.error(err);
        toast.error('Impossible de charger les statistiques');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const totals = stats?.totals;
  const social = stats?.social;
  const memberSince = formatMemberSince(stats?.member_since);

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black text-white' : 'bg-white text-gray-900'} pb-32`}>
      <Navigation />
      <header className="sticky top-0 z-30 backdrop-blur-md bg-black/60 border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="statistics-back-btn"
          aria-label="Retour"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-magenta-400" />
          {tr(t, 'statistics.title', 'Statistiques et monétisation')}
        </h1>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-6">
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-2 border-magenta-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && stats && (
          <>
            <p className="text-sm text-gray-400 mb-2">
              {tr(t, 'statistics.subtitle', "Suivez la portée de votre contenu et l'engagement de votre audience.")}
            </p>
            {memberSince && (
              <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-4">
                <Calendar className="w-3.5 h-3.5" />
                {tr(t, 'statistics.memberSince', 'Membre depuis')} {memberSince}
              </p>
            )}

            {/* Totaux globaux */}
            <SectionTitle icon={TrendingUp}>{tr(t, 'statistics.global', "Vue d'ensemble")}</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Heart} label={tr(t, 'statistics.totalLikes', "J'aime reçus")} value={totals?.likes} accent="magenta" testId="stat-total-likes" />
              <StatCard icon={MessageCircle} label={tr(t, 'statistics.totalComments', 'Commentaires')} value={totals?.comments} accent="cyan" testId="stat-total-comments" />
              <StatCard icon={Share2} label={tr(t, 'statistics.totalShares', 'Partages')} value={totals?.shares} accent="green" testId="stat-total-shares" />
              <StatCard icon={Eye} label={tr(t, 'statistics.totalPlays', 'Lectures / Vues')} value={totals?.plays_or_views} accent="amber" testId="stat-total-plays" />
            </div>

            {/* Audience */}
            <SectionTitle icon={Users}>{tr(t, 'statistics.audience', 'Audience')}</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={Users} label={tr(t, 'statistics.followers', 'Abonnés')} value={social?.followers} accent="violet" testId="stat-followers" />
              <StatCard icon={UserPlus} label={tr(t, 'statistics.following', 'Abonnements')} value={social?.following} accent="violet" testId="stat-following" />
              <StatCard icon={Bookmark} label={tr(t, 'statistics.saved', 'Enregistrés')} value={social?.saved_items} accent="cyan" testId="stat-saved" />
              <StatCard icon={Play} label={tr(t, 'statistics.activeStories', 'Stories actives')} value={social?.active_stories} accent="magenta" testId="stat-stories" />
            </div>

            {/* Gim Toli */}
            <SectionTitle icon={Mic}>{tr(t, 'statistics.gimToli', 'Gim Toli')}</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard icon={Mic} label={tr(t, 'statistics.posts', 'Publications')} value={stats.gim_toli?.count} accent="magenta" testId="stat-gimtoli-count" />
              <StatCard icon={Heart} label={tr(t, 'statistics.likes', "J'aime")} value={stats.gim_toli?.likes} accent="magenta" testId="stat-gimtoli-likes" />
              <StatCard icon={MessageCircle} label={tr(t, 'statistics.comments', 'Commentaires')} value={stats.gim_toli?.comments} accent="cyan" testId="stat-gimtoli-comments" />
              <StatCard icon={Share2} label={tr(t, 'statistics.shares', 'Partages')} value={stats.gim_toli?.shares} accent="green" testId="stat-gimtoli-shares" />
              <StatCard icon={Play} label={tr(t, 'statistics.plays', 'Lectures')} value={stats.gim_toli?.plays} accent="amber" testId="stat-gimtoli-plays" />
            </div>

            {/* Films courts */}
            <SectionTitle icon={Film}>{tr(t, 'statistics.shortFilms', 'Films courts')}</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard icon={Film} label={tr(t, 'statistics.posts', 'Publications')} value={stats.short_videos?.count} accent="cyan" testId="stat-shorts-count" />
              <StatCard icon={Heart} label={tr(t, 'statistics.likes', "J'aime")} value={stats.short_videos?.likes} accent="magenta" testId="stat-shorts-likes" />
              <StatCard icon={MessageCircle} label={tr(t, 'statistics.comments', 'Commentaires')} value={stats.short_videos?.comments} accent="cyan" testId="stat-shorts-comments" />
              <StatCard icon={Share2} label={tr(t, 'statistics.shares', 'Partages')} value={stats.short_videos?.shares} accent="green" testId="stat-shorts-shares" />
              <StatCard icon={Eye} label={tr(t, 'statistics.views', 'Vues')} value={stats.short_videos?.views} accent="amber" testId="stat-shorts-views" />
            </div>

            {/* Vidéos partagées */}
            <SectionTitle icon={Film}>{videoTexts(language).videosTab}</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="stats-shared-videos">
              <StatCard icon={Film} label={tr(t, 'statistics.posts', 'Publications')} value={stats.shared_videos?.count} accent="cyan" testId="stat-videos-count" />
              <StatCard icon={Heart} label={tr(t, 'statistics.likes', "J'aime")} value={stats.shared_videos?.likes} accent="magenta" testId="stat-videos-likes" />
              <StatCard icon={MessageCircle} label={tr(t, 'statistics.comments', 'Commentaires')} value={stats.shared_videos?.comments} accent="cyan" testId="stat-videos-comments" />
              <StatCard icon={Share2} label={tr(t, 'statistics.shares', 'Partages')} value={stats.shared_videos?.shares} accent="green" testId="stat-videos-shares" />
              <StatCard icon={Bookmark} label={videoTexts(language).saved} value={stats.shared_videos?.saves} accent="violet" testId="stat-videos-saves" />
              <StatCard icon={Eye} label={tr(t, 'statistics.views', 'Vues')} value={stats.shared_videos?.views} accent="amber" testId="stat-videos-views" />
            </div>

            {/* Podcasts */}
            <SectionTitle icon={Headphones}>{tr(t, 'statistics.podcasts', 'Podcasts')}</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard icon={Headphones} label={tr(t, 'statistics.posts', 'Publications')} value={stats.podcasts?.count} accent="violet" testId="stat-podcasts-count" />
              <StatCard icon={Heart} label={tr(t, 'statistics.likes', "J'aime")} value={stats.podcasts?.likes} accent="magenta" testId="stat-podcasts-likes" />
              <StatCard icon={MessageCircle} label={tr(t, 'statistics.comments', 'Commentaires')} value={stats.podcasts?.comments} accent="cyan" testId="stat-podcasts-comments" />
              <StatCard icon={Share2} label={tr(t, 'statistics.shares', 'Partages')} value={stats.podcasts?.shares} accent="green" testId="stat-podcasts-shares" />
              <StatCard icon={Play} label={tr(t, 'statistics.plays', 'Lectures')} value={stats.podcasts?.plays} accent="amber" testId="stat-podcasts-plays" />
            </div>

            {/* Monétisation */}
            <SectionTitle icon={DollarSign}>{tr(t, 'monetization.title', 'Monétisation')}</SectionTitle>
            <MonetizationSection />
          </>
        )}
      </div>
    </div>
  );
}
